import {
  idempotencyTtlMsFor,
  isHighRiskMutationPath,
  messageFromApiError,
  mutationFingerprint,
} from '@lighthouse/shared';
import { useAuthStore } from '../hooks/useAuthStore';
import { getCountryCode } from '../utils/country';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export { API_URL };

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const recentMutationKeys = new Map<string, { key: string; expiresAt: number }>();
const INTENT_STORAGE_PREFIX = 'lh-idempotency:';

export function createIdempotencyKey(): string {
  return `lh-web-${crypto.randomUUID()}`;
}

function readPersistedKey(fingerprint: string): string | null {
  try {
    const raw = sessionStorage.getItem(`${INTENT_STORAGE_PREFIX}${fingerprint}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { key?: string; expiresAt?: number };
    if (!parsed.key || !parsed.expiresAt || parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(`${INTENT_STORAGE_PREFIX}${fingerprint}`);
      return null;
    }
    return parsed.key;
  } catch {
    return null;
  }
}

function persistKey(fingerprint: string, key: string, expiresAt: number): void {
  try {
    sessionStorage.setItem(
      `${INTENT_STORAGE_PREFIX}${fingerprint}`,
      JSON.stringify({ key, expiresAt })
    );
  } catch {
    // Private mode / quota — memory map still covers double-clicks in this tab.
  }
}

function idempotencyKeyFor(endpoint: string, method: string, body: BodyInit | null | undefined) {
  if (typeof body !== 'string') return createIdempotencyKey();
  const fingerprint = mutationFingerprint(method, endpoint, body);
  const ttlMs = idempotencyTtlMsFor(endpoint);
  const now = Date.now();

  const existing = recentMutationKeys.get(fingerprint);
  if (existing && existing.expiresAt > now) return existing.key;

  if (isHighRiskMutationPath(endpoint)) {
    const persisted = readPersistedKey(fingerprint);
    if (persisted) {
      recentMutationKeys.set(fingerprint, { key: persisted, expiresAt: now + ttlMs });
      return persisted;
    }
  }

  const key = createIdempotencyKey();
  const expiresAt = now + ttlMs;
  recentMutationKeys.set(fingerprint, { key, expiresAt });
  if (isHighRiskMutationPath(endpoint)) {
    persistKey(fingerprint, key, expiresAt);
  } else {
    setTimeout(() => {
      if (recentMutationKeys.get(fingerprint)?.key === key) recentMutationKeys.delete(fingerprint);
    }, ttlMs);
  }
  return key;
}

/** Mint once per user gesture for irreversible flows; reuse on retry until cleared. */
export function beginMutationIntent(intentId: string): string {
  const storageKey = `${INTENT_STORAGE_PREFIX}intent:${intentId}`;
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
  } catch {
    // fall through
  }
  const key = createIdempotencyKey();
  try {
    sessionStorage.setItem(storageKey, key);
  } catch {
    // ignore
  }
  return key;
}

export function clearMutationIntent(intentId: string): void {
  try {
    sessionStorage.removeItem(`${INTENT_STORAGE_PREFIX}intent:${intentId}`);
  } catch {
    // ignore
  }
}

async function waitForIdempotentResponse(request: () => Promise<Response>): Promise<Response> {
  let response = await request();
  for (let attempt = 0; attempt < 3 && response.status === 409; attempt++) {
    if (!response.headers.has('Retry-After')) break;
    await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    response = await request();
  }
  return response;
}

export async function refreshAccessToken(): Promise<string | null> {
  const { setAccessToken, logout } = useAuthStore.getState();

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Client-Type': 'web' },
      credentials: 'include',
    });

    if (!response.ok) {
      logout();
      return null;
    }

    const data = (await response.json()) as {
      tokens: { accessToken: string; expiresIn?: number };
    };
    setAccessToken(data.tokens.accessToken);
    return data.tokens.accessToken;
  } catch {
    logout();
    return null;
  }
}

export type ApiRequestOptions = RequestInit & {
  /** Explicit key for one user intent (booking pay, cancel confirm, etc.). */
  idempotencyKey?: string;
};

export async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const { accessToken } = useAuthStore.getState();
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const { idempotencyKey, ...requestInit } = options;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-Country-Code': getCountryCode(),
    'X-Client-Type': 'web',
    ...(requestInit.headers as Record<string, string>),
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const method = (requestInit.method ?? 'GET').toUpperCase();
  if (MUTATING_METHODS.has(method) && !isFormData && !headers['Idempotency-Key']) {
    headers['Idempotency-Key'] =
      idempotencyKey ?? idempotencyKeyFor(endpoint, method, requestInit.body);
  }

  const send = () =>
    fetch(`${API_URL}${endpoint}`, {
      ...requestInit,
      headers,
      credentials: 'include',
    });
  let response = await waitForIdempotentResponse(send);

  if (response.status === 401 && accessToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      response = await waitForIdempotentResponse(send);
    }
  }

  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as {
      message?: string;
      details?: { path?: string; message?: string }[];
    } | null;
    throw new Error(messageFromApiError(error, response.status));
  }

  return response.json() as Promise<T>;
}

export async function logoutSession(): Promise<void> {
  try {
    await apiRequest('/auth/logout', { method: 'POST' });
  } catch {
    // proceed with local logout even if API call fails
  }
  useAuthStore.getState().logout();
}

export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint),
  post: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : data !== undefined ? JSON.stringify(data) : undefined,
    }),
  put: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  patch: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    }),
  delete: <T>(endpoint: string, options?: ApiRequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};
